import { merge } from 'lodash';
import { mmToPixels } from './printDuplicateMap';
import { DEFAULT_DOTS_PER_INCH } from '../../constants';

const alignLeftTable = {
    C: 'center',
    L: 'start',
    R: 'flex-end'
};
const alignTopTable = {
    C: 'center',
    T: 'start',
    B: 'flex-end'
};
function getValByUnit(value, dpi, unit) {
    if (unit === 'mm') {
        return { value: mmToPixels(value, dpi), unit: 'px' };
    }
    return { value, unit };
}

export function calculateDPICorrection(value, printDPI) {
    return value * (printDPI / DEFAULT_DOTS_PER_INCH);
}

export function paperSettings(settings, formatName) {
    if (!settings) {
        return undefined;
    }
    if (!formatName) {
        return undefined;
    }

    const pageSettingData = settings.templates.find(format => format.title === formatName);
    const defaultsettings = settings.default;
    if (!pageSettingData) {
        return undefined;
    }
    return {
        ...pageSettingData,
        settings: pageSettingData.settings ? merge({}, defaultsettings, pageSettingData.settings) : defaultsettings
    };
}

function createCss(formatSettings, dpi) {
    if (!formatSettings) {
        return undefined;
    }
    const components = {};
    for (const property in formatSettings.settings.components) {
        if (Object.prototype.hasOwnProperty.call(formatSettings.settings.components, property)) {
            if (property === 'chart') {
                components.map = getMapSettings(formatSettings, dpi);
            } else {
                components[property] = componentCssSettings(formatSettings, dpi, property);
            }
        }
    }
    const paper = getPageSettings(formatSettings, dpi);
    return {
        paper,
        ...components
    };
}

function getPageSettings(printConfig, dpi) {
    const width = getValByUnit(printConfig.width, dpi, printConfig.unit);
    const height = getValByUnit(printConfig.height, dpi, printConfig.unit);
    const leftMargin = getValByUnit(printConfig.margins.left, dpi, printConfig.unit);
    const rightMargin = getValByUnit(printConfig.margins.right, dpi, printConfig.unit);
    const topMargin = getValByUnit(printConfig.margins.top, dpi, printConfig.unit);
    const bottomMargin = getValByUnit(printConfig.margins.bottom, dpi, printConfig.unit);
    return {
        width: width.value,
        height: height.value,
        left: leftMargin.value,
        right: rightMargin.value,
        top: topMargin.value,
        bottom: bottomMargin.value,
        _props: printConfig
    };
}

function getMapSettings(printConfig, dpi) {
    const chartSettings = printConfig.settings.components.chart;
    const mapWidth = getValByUnit(chartSettings.width, dpi, chartSettings.unit);
    const mapHeight = getValByUnit(chartSettings.height, dpi, chartSettings.unit);
    const mapxMargin = getValByUnit(chartSettings.position.x, dpi, chartSettings.unit);
    const mapyMargin = getValByUnit(chartSettings.position.y, dpi, chartSettings.unit);
    const mapAlignLeft = chartSettings.position.snap.charAt(0);
    const mapAlignTop = chartSettings.position.snap.charAt(1);
    const css = chartSettings.css;

    const mapMargins = {
        left: `${mapAlignLeft === 'R' ? '-' : ''}${mapxMargin.value}${mapxMargin.unit}`,
        top: `${mapAlignTop === 'B' ? '-' : ''}${mapyMargin.value}${mapyMargin.unit}`
    };

    const mapAlign = { parent: getAlignCssProperties(mapAlignLeft, mapAlignTop) };
    return {
        css: {
            parent: { ...mapAlign.parent },
            child: {
                ...mapMargins,
                position: 'relative',
                boxSizing: 'content-box',
                width: `${mapWidth.value}${mapWidth.unit}`,
                height: `${mapHeight.value}${mapHeight.unit}`,
                ...css
            }

        }
    };
}

function componentCssSettings(printConfig, dpi, name) {
    const componentSettings = printConfig.settings.components[name];
    if (!componentSettings) {
        return undefined;
    }
    const width = componentSettings.width ? getValByUnit(componentSettings.width, dpi, componentSettings.unit) : { value: 'auto', unit: '' };
    const height = componentSettings.height ? getValByUnit(componentSettings.height, dpi, componentSettings.unit) : { value: 'auto', unit: '' };
    const alignLeft = componentSettings.position.snap.charAt(0);
    const alignTop = componentSettings.position.snap.charAt(1);
    const XMargin = getValByUnit(componentSettings.position.x, dpi, componentSettings.unit);
    const YMargin = getValByUnit(componentSettings.position.y, dpi, componentSettings.unit);
    const css = componentSettings.css;
    if (css.textShadow) {
        css.textShadow = `${css.textShadow} !important`;
    }
    const margins = {
        left: alignLeft === 'L' ? `${XMargin.value}${XMargin.unit}` : 'auto',
        right: alignLeft === 'R' ? `${XMargin.value}${XMargin.unit}` : 'auto',
        top: alignTop === 'T' ? `${YMargin.value}${YMargin.unit}` : 'auto',
        bottom: alignTop === 'B' ? `${YMargin.value}${YMargin.unit}` : 'auto'
    };
    const fontSize = componentSettings.fontSize && calculateDPICorrection(componentSettings.fontSize.size, dpi) + componentSettings.fontSize.unit;
    const align = { parent: getAlignCssProperties(alignLeft, alignTop) };

    const content = componentSettings.content;
    const config = componentSettings.config || {};

    return {
        css: {
            parent: { ...align.parent },
            child: {
                ...margins,
                position: 'relative',
                lineHeight: '1em',
                width: `${width.value}${width.unit}`,
                height: `${height.value}${height.unit}`,
                ...css,
                fontSize
            }
        },
        content,
        ...config
    };
}

const getAlignCssProperties = (alignLeft, alignTop) =>  ({
    display: 'flex',
    justifyContent: alignLeftTable[alignLeft],
    alignItems: alignTopTable[alignTop],
    width: '100%',
    height: '100%'
});


export { createCss };
